import { useRef, type KeyboardEvent } from 'react'
import { toggleClass } from '@/components/ui/toggle'
import { cn } from '@/lib/cn'
import { fmtInt } from '@/lib/format'
import { useMore } from '@/lib/useMore'

export interface TabItem<T extends string> {
  id: T
  label: string
  /** a count after the label (tabular figures); 0 is shown, never hidden */
  count?: number
  /** the full name for screen readers, e.g. «Видео: 471 модель, включено 391» */
  ariaLabel?: string
  /** `warn`: the count needs a look (new payout requests) — a warn chip, as in the rail. The
   *  label and `ariaLabel` carry the words (rule 21: never the colour alone). */
  tone?: 'warn'
}

/** The id of a tab button, for the panel's aria-labelledby. */
export const tabId = (prefix: string, id: string) => `${prefix}-tab-${id}`

/**
 * A WAI-ARIA tablist (design §6.12): the selected tab is the FilterChip formula — cyan tint,
 * cyan line, ink-1 600 (decision D1; the solid cyan fill is the screen's one primary action) —
 * the rest are neutral; one Tab stop (roving tabIndex), ←/→ and Home/End move and select. Tabs wrap onto
 * more lines at 320 px instead of scrolling the page; `scrollOnPhone` keeps them in one row that
 * scrolls sideways inside itself below `md` (its right edge fades while more tabs are off it) —
 * for a long list above a table, so the first rows stay on the first screen (WP-15). The panel is the caller's
 * (`role="tabpanel"`, `id={panelId}`, `aria-labelledby={tabId(idPrefix, value)}`).
 */
export function Tabs<T extends string>({
  items,
  value,
  onChange,
  label,
  idPrefix,
  panelId,
  size = 'sm',
  scrollOnPhone = false,
  className,
}: {
  items: readonly TabItem<T>[]
  value: T
  onChange: (id: T) => void
  /** the tablist's name («Типы моделей») */
  label: string
  idPrefix: string
  panelId?: string
  /** `md` (40 px) in a filter row; `sm` (32 px) elsewhere */
  size?: 'sm' | 'md'
  /** below `md`: one row scrolling sideways instead of wrapping */
  scrollOnPhone?: boolean
  className?: string
}) {
  const refs = useRef(new Map<string, HTMLButtonElement>())
  const [rowRef, more] = useMore<HTMLDivElement>('x')

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const at = items.findIndex((t) => t.id === value)
    let next = -1
    if (e.key === 'ArrowRight') next = (at + 1) % items.length
    else if (e.key === 'ArrowLeft') next = (at - 1 + items.length) % items.length
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = items.length - 1
    if (next < 0) return
    e.preventDefault()
    const t = items[next]
    if (!t) return
    onChange(t.id)
    const el = refs.current.get(t.id)
    el?.focus()
    if (scrollOnPhone) el?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }

  return (
    <div
      ref={scrollOnPhone ? rowRef : undefined}
      role="tablist"
      aria-label={label}
      className={cn(
        'flex flex-wrap gap-2',
        scrollOnPhone && 'max-md:flex-nowrap max-md:overflow-x-auto max-md:[&>*]:shrink-0',
        scrollOnPhone && more && 'max-md:fade-end',
        className,
      )}
      onKeyDown={onKeyDown}
    >
      {items.map((t) => {
        const selected = t.id === value
        return (
          <button
            key={t.id}
            ref={(el) => {
              if (el) refs.current.set(t.id, el)
              else refs.current.delete(t.id)
            }}
            id={tabId(idPrefix, t.id)}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={panelId}
            aria-label={t.ariaLabel}
            tabIndex={selected ? 0 : -1}
            className={toggleClass(selected, size)}
            onClick={() => onChange(t.id)}
          >
            {t.label}
            {t.count !== undefined &&
              (t.tone === 'warn' && t.count > 0 ? (
                <span className="tnum inline-flex h-5 min-w-5 items-center justify-center rounded-xs border border-warn-line bg-warn-soft px-1.5 text-micro text-warn">
                  {fmtInt(t.count)}
                </span>
              ) : (
                <span className="tnum font-normal">{fmtInt(t.count)}</span>
              ))}
          </button>
        )
      })}
    </div>
  )
}
